import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import CardView from "../../components/molecules/CardView/CardView";
import DraggableTable from "../../components/organisms/DraggableTable/DraggableTable";
import Button from "../../components/atoms/Button/Button";
import Input from "../../components/atoms/Input/Input";
import TextArea from "../../components/atoms/TextArea/TextArea";
import ValidationMessage from "../../components/atoms/ValidationMessage/ValidationMessage";
import "./Suppliers.scss";
import { servicesSuppliers } from "../../services/suppliers";
import AnimatedCard from "../../components/molecules/AnimatedCard/AnimatedCard";
import ConfirmDialog from "../../components/molecules/ConfirmDialog/ConfirmDialog";

const SupplierGroups = () => {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);

    const [createGroups, setCreateGroups] = useState({
        name: "",
        description: "",
    });

    const [loading, setLoading] = useState(false);
    // const [pagination, setPagination] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [emptyFields, setEmptyFields] = useState({});
    const [editingGroup, setEditingGroup] = useState(null); // null cuando es crear, objeto cuando es editar
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        itemToDelete: null,
    });

    const getSuppliersGroup = async () => {
        setLoading(true);
        try {
            const response = await servicesSuppliers.getSuppliersGroups();

            if (response.success) {
                setGroups(response.data);
            }
        } catch (error) {
            console.error("Error al obtener los grupos de proveedores:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSuppliersGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Definir las columnas de la tabla
    const columns = [
        {
            field: "name",
            header: "Nombre del Grupo",
            sortable: true,
            width: "minmax(180px, 2fr)",
        },
        {
            field: "description",
            header: "Descripción",
            sortable: true,
            width: "minmax(200px, 3fr)",
        },
        {
            field: "suppliersCount",
            header: "Proveedores",
            sortable: true,
            width: "minmax(120px, 1fr)",
            body: (rowData) => {
                return (
                    <span className="suppliers__badge">
                        {rowData.suppliersCount}
                    </span>
                );
            },
        },
        {
            field: "createdAt",
            header: "Fecha",
            sortable: true,
            width: "minmax(130px, 1fr)",
            body: (rowData) => {
                return new Date(rowData.createdAt).toLocaleDateString("es-ES");
            },
        },
    ];

    const handleEdit = (rowData) => {
        setEditingGroup(rowData);
        setCreateGroups({
            name: rowData.name,
            description: rowData.description,
        });
        setOpenModal(true);
    };

    const handleDelete = (rowData) => {
        setConfirmDialog({
            isOpen: true,
            itemToDelete: rowData,
        });
    };

    const confirmDelete = async () => {
        const rowData = confirmDialog.itemToDelete;
        if (!rowData || !rowData.id) return;

        const toastId = toast.loading("Eliminando grupo...");

        try {
            const response = await servicesSuppliers.deleteSupplierGroup(
                rowData.id
            );

            if (response.success) {
                toast.success("Grupo eliminado exitosamente", { id: toastId });
                setGroups(groups.filter((group) => group.id !== rowData.id));
            } else {
                toast.error(response.message || "Error al eliminar el grupo", {
                    id: toastId,
                });
            }
        } catch (error) {
            toast.error("Error al eliminar el grupo", { id: toastId });
        }
    };

    // const handlePageChange = (page, rowsPerPage) => {
    //     // Aquí harías la llamada a la API con la nueva página
    // };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCreateGroups((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Limpiar el error del campo cuando el usuario empiece a escribir
        setEmptyFields((prev) => ({
            ...prev,
            [name]: false,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar campos vacíos
        const errors = {};
        if (!createGroups.name.trim()) errors.name = true;
        if (!createGroups.description.trim()) errors.description = true;

        if (Object.keys(errors).length > 0) {
            setEmptyFields(errors);
            toast.error("Por favor, completa todos los campos requeridos");
            return;
        }

        try {
            let response;

            // Si estamos editando, usar update, si no, crear
            if (editingGroup) {
                response = await servicesSuppliers.updateSupplierGroup(
                    editingGroup.id,
                    createGroups
                );
            } else {
                response = await servicesSuppliers.createSupplierGroup(
                    createGroups
                );
            }

            if (response.success) {
                toast.success(
                    editingGroup
                        ? "Grupo actualizado exitosamente"
                        : "Grupo creado exitosamente"
                );
                handleCloseModal();
                getSuppliersGroup(); // Recargar la lista
            } else {
                toast.error(
                    response.message ||
                        `Error al ${
                            editingGroup ? "actualizar" : "crear"
                        } el grupo`
                );
            }
        } catch (error) {
            console.error("Error al procesar el grupo:", error);
            toast.error(
                `Error al ${editingGroup ? "actualizar" : "crear"} el grupo`
            );
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setCreateGroups({ name: "", description: "" });
        setEmptyFields({});
        setEditingGroup(null);
    };

    const handleOpenCreateModal = () => {
        setEditingGroup(null);
        setCreateGroups({ name: "", description: "" });
        setEmptyFields({});
        setOpenModal(true);
    };

    const handleRowClick = (rowData) => {
        // Navegar a la vista de proveedores del grupo
        navigate(
            `/proveedores/${encodeURIComponent(rowData.name)}/${rowData.id}`
        );
    };

    const handleReorder = async (reorderedData) => {
        // Actualizar el estado local inmediatamente para mejor UX
        setGroups(reorderedData);

        // Preparar los datos para el API
        const items = reorderedData.map((item, index) => ({
            id: item.id,
            order: index,
        }));

        try {
            const response = await servicesSuppliers.reorderSupplierGroups(
                items
            );

            if (response.success) {
                toast.success("Orden actualizado exitosamente");
            } else {
                // Si falla, revertir el cambio
                toast.error(response.message || "Error al actualizar el orden");
                getSuppliersGroup(); // Recargar desde el servidor
            }
        } catch (error) {
            console.error("Error al reordenar grupos:", error);
            toast.error("Error al actualizar el orden");
            getSuppliersGroup(); // Recargar desde el servidor
        }
    };

    return (
        <CardView className="suppliers">
            <div className="suppliers__header">
                <h1 className="suppliers__title">Grupos de Proveedores</h1>
                <Button
                    text={"Nuevo Grupo"}
                    className="suppliers__addBtn degrad"
                    onClick={handleOpenCreateModal}
                />
            </div>

            <DraggableTable
                data={groups}
                columns={columns}
                loading={loading}
                onReorder={handleReorder}
                showActions={true}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onRowClick={handleRowClick}
                emptyMessage="No hay grupos registrados"
                striped={true}
                hoverable={true}
            />

            {openModal && (
                <AnimatedCard
                    setClose={handleCloseModal}
                    title={editingGroup ? "Editar Grupo" : "Crear Nuevo Grupo"}
                    size="md"
                    variant="elegant"
                    coloredHeader={true}
                >
                    <form onSubmit={handleSubmit} className="suppliers__form">
                        <ValidationMessage show={emptyFields.name}>
                            <Input
                                type="text"
                                name="name"
                                text="Nombre del Grupo"
                                placeholder="Ej: Proveedores de Bebidas"
                                value={createGroups.name}
                                onChange={handleChange}
                                show={emptyFields.name}
                            />
                        </ValidationMessage>

                        <ValidationMessage show={emptyFields.description}>
                            <TextArea
                                name="description"
                                text="Descripción"
                                placeholder="Describe el grupo de proveedores..."
                                value={createGroups.description}
                                onChange={handleChange}
                                show={emptyFields.description}
                                rows={4}
                            />
                        </ValidationMessage>

                        <div className="suppliers__form-actions">
                            <Button
                                type="button"
                                text="Cancelar"
                                className="suppliers__cancel-btn"
                                onClick={handleCloseModal}
                            />
                            <Button
                                type="submit"
                                text={
                                    editingGroup ? "Actualizar" : "Crear Grupo"
                                }
                                className="suppliers__submit-btn degrad"
                            />
                        </div>
                    </form>
                </AnimatedCard>
            )}

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() =>
                    setConfirmDialog({ isOpen: false, itemToDelete: null })
                }
                onConfirm={confirmDelete}
                title="Eliminar Grupo"
                message="¿Estás seguro de que deseas eliminar este grupo de proveedores?"
                itemName={confirmDialog.itemToDelete?.name}
                confirmText="Eliminar"
                cancelText="Cancelar"
                type="danger"
            />

            <Toaster position="top-right" richColors />
        </CardView>
    );
};

export default SupplierGroups;
